import React, { useState } from 'react';
import { authenticatedFetch } from '../utils/cloudFunctions';
import './StoryAiCreator.css';

const getInputLabel = (contentTypeId) => {
    const labels = {
        'reel': 'What type of Reel do you want to create?',
        'story': 'What should your Story be about?',
        'tiktok': 'What\'s your TikTok concept?',
        'youtube-short': 'What skill/tip do you want to teach?',
        'vsl': 'What product/service are you selling?',
        'youtube-tutorial': 'What do you want to teach step-by-step?',
        'youtube-framework': 'What system/framework do you want to explain?',
        'youtube-vlog': 'What experience/story do you want to share?',
        'linkedin-video': 'What professional insight do you want to share?',
        'short-film': 'What story do you want to tell?',
        'ad': 'What are you advertising?'
    };
    return labels[contentTypeId] || 'Describe your content idea:';
};

const getInputPlaceholder = (contentTypeId) => {
    const placeholders = {
        'reel': 'e.g., "Life hack for saving money", "Rating viral TikTok trends", "My morning routine but make it aesthetic"',
        'story': 'e.g., "This or that outfit choice", "Guess my favorite coffee shop", "Rate my playlist 1-10"',
        'tiktok': 'e.g., "POV: You\'re the main character", "Things nobody tells you about...", "Me explaining to my mom why..."',
        'youtube-short': 'e.g., "iPhone tricks nobody knows", "Excel shortcuts for beginners", "How to tie perfect shoelaces"',
        'vsl': 'e.g., "Online course about digital marketing", "Weight loss supplement", "Business coaching program"',
        'youtube-tutorial': 'e.g., "How to edit videos in Premiere Pro", "Baking the perfect sourdough bread", "Building a website from scratch"',
        'youtube-framework': 'e.g., "The 5-step productivity system", "My content creation framework", "How to build habits that stick"',
        'youtube-vlog': 'e.g., "My first solo trip to Japan", "Moving out at 18", "Starting my own business journey"',
        'linkedin-video': 'e.g., "Leadership lessons from my startup failure", "Remote work productivity tips", "Industry trends in 2024"',
        'short-film': 'e.g., "A person finds a mysterious letter", "Two strangers meet at a coffee shop", "Last day before moving abroad"',
        'ad': 'e.g., "Fitness app for busy professionals", "Sustainable fashion brand", "Local restaurant grand opening"'
    };
    return placeholders[contentTypeId] || 'Tell us your content idea...';
};

const getInputHints = (contentTypeId) => {
    const hints = {
        'reel': '💡 Think: What would make people save this? What\'s trending right now?',
        'story': '💡 Think: What would make people want to vote/respond immediately?',
        'tiktok': '💡 Think: What sound/trend could you use? What\'s relatable to your audience?',
        'youtube-short': '💡 Think: What problem does this solve? What value does it provide?',
        'vsl': '💡 Think: What problem does your product solve? Who\'s your target customer?',
        'youtube-tutorial': '💡 Think: What skill takes 10-20 minutes to learn? What tools are needed?',
        'youtube-framework': '💡 Think: What system has worked for you? What process can you teach?',
        'youtube-vlog': '💡 Think: What personal story has a lesson? What experience was transformative?',
        'linkedin-video': '💡 Think: What professional insight would your network value?',
        'short-film': '💡 Think: What emotion do you want to evoke? What message should it convey?',
        'ad': '💡 Think: What makes your product unique? What\'s the main benefit?'
    };
    return hints[contentTypeId] || '💡 Be specific about your target audience and main message';
};

const StoryAiCreator = ({ onBack, contentType }) => {
    const [ideaDescription, setIdeaDescription] = useState('');
    const [noIdea, setNoIdea] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [storyStarters, setStoryStarters] = useState([]);
    const [selectedStarter, setSelectedStarter] = useState(null);
    const [error, setError] = useState(null);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [targetAudience, setTargetAudience] = useState('');
    const [contentGoal, setContentGoal] = useState('');
    const [trendLevel, setTrendLevel] = useState('current');

    const handleGenerateStories = async () => {
        if (!noIdea && !ideaDescription.trim()) {
            setError('Please provide an idea description or toggle "I have no idea"');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Determine if we're in development or production
            const isDevelopment = window.location.hostname === 'localhost' || 
                                window.location.hostname === '127.0.0.1' || 
                                window.location.hostname.includes('192.168.') ||
                                process.env.NODE_ENV === 'development';
            
            const baseUrl = isDevelopment 
                ? 'https://us-central1-bonusclub-dev.cloudfunctions.net'
                : 'https://us-central1-bonusclub-ab5e2.cloudfunctions.net';
            
            const response = await authenticatedFetch(`${baseUrl}/createAiStory`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ideaDescription: ideaDescription.trim(),
                    noIdea: noIdea,
                    contentType: contentType,
                    targetAudience: targetAudience.trim(),
                    contentGoal: contentGoal.trim(),
                    trendLevel: trendLevel,
                    language: 'German',
                    model: 'gpt-4o-mini',
                    temperature: 0.8,
                    maxTokens: 2000
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.details || 'Failed to generate stories');
            }

            const data = await response.json();
            setStoryStarters(data.starters || []);
            
        } catch (err) {
            console.error('Error generating stories:', err);
            setError(err.message || 'Error generating story starters');
        } finally {
            setIsLoading(false);
        }
    };

    const handleStarterSelect = (starter) => {
        setSelectedStarter(starter);
        // TODO: Implement navigation to storyboard creation
        console.log('Selected starter:', starter);
    };

    const handleReset = () => {
        setStoryStarters([]);
        setSelectedStarter(null);
        setError(null);
    };

    if (selectedStarter) {
        return (
            <div className="story-ai-creator-container">
                <div className="story-ai-creator-header">
                    <button 
                        className="story-ai-creator-back-button"
                        onClick={() => setSelectedStarter(null)}
                    >
                        ← Back to Starters
                    </button>
                    <h2 className="story-ai-creator-title">Selected Story</h2>
                </div>
                
                <div className="story-ai-creator-selected">
                    <div className="story-ai-creator-selected-card">
                        <div className="story-ai-creator-selected-genre">{selectedStarter.genre}</div>
                        <h3 className="story-ai-creator-selected-description">{selectedStarter.description}</h3>
                        <div className="story-ai-creator-selected-points">
                            {selectedStarter.points.map((point, index) => (
                                <div key={index} className="story-ai-creator-selected-point">
                                    <span className="story-ai-creator-point-number">{index + 1}.</span>
                                    <span className="story-ai-creator-point-text">{point}</span>
                                </div>
                            ))}
                        </div>
                        <div className="story-ai-creator-selected-actions">
                            <button className="story-ai-creator-continue-button">
                                Continue with this Story
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (storyStarters.length > 0) {
        return (
            <div className="story-ai-creator-container">
                <div className="story-ai-creator-header">
                    <button 
                        className="story-ai-creator-back-button"
                        onClick={handleReset}
                    >
                        ← New Ideas
                    </button>
                    <h2 className="story-ai-creator-title">
                        Choose Your {contentType?.name || 'Content'} Concept
                    </h2>
                </div>
                
                <div className="story-ai-creator-starters-grid">
                    {storyStarters.map((starter) => (
                        <div 
                            key={starter.id} 
                            className="story-ai-creator-starter-card"
                            onClick={() => handleStarterSelect(starter)}
                        >
                            <div className="story-ai-creator-starter-genre">{starter.genre}</div>
                            <h3 className="story-ai-creator-starter-description">{starter.description}</h3>
                            <div className="story-ai-creator-starter-points">
                                {starter.points.map((point, index) => (
                                    <div key={index} className="story-ai-creator-starter-point">
                                        {index + 1}. {point}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="story-ai-creator-container">
            <div className="story-ai-creator-header">
                <button 
                    className="story-ai-creator-back-button"
                    onClick={onBack}
                >
                    ← Back
                </button>
                <h2 className="story-ai-creator-title">
                    AI {contentType?.name || 'Content'} Creator
                </h2>
            </div>

            <div className="story-ai-creator-form">
                <div className="story-ai-creator-input-section">
                    <h3 className="story-ai-creator-section-title">Tell us your idea</h3>
                    
                    <div className="story-ai-creator-toggle-section">
                        <label className="story-ai-creator-toggle">
                            <input
                                type="checkbox"
                                checked={noIdea}
                                onChange={(e) => setNoIdea(e.target.checked)}
                            />
                            <span className="story-ai-creator-toggle-slider"></span>
                            <span className="story-ai-creator-toggle-text">
                                I have no idea. Please come up with something
                            </span>
                        </label>
                    </div>

                    {!noIdea && (
                        <div className="story-ai-creator-textarea-section">
                            <label className="story-ai-creator-label">
                                {getInputLabel(contentType?.id)}
                            </label>
                            <textarea
                                className="story-ai-creator-textarea"
                                value={ideaDescription}
                                onChange={(e) => setIdeaDescription(e.target.value)}
                                placeholder={getInputPlaceholder(contentType?.id)}
                                rows={6}
                                disabled={isLoading}
                            />
                            <div className="story-ai-creator-input-hints">
                                {getInputHints(contentType?.id)}
                            </div>
                        </div>
                    )}

                    <div className="story-ai-creator-advanced-section">
                        <button 
                            className="story-ai-creator-advanced-toggle"
                            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                        >
                            🎯 Advanced Options {showAdvancedOptions ? '▼' : '▶'}
                        </button>
                        
                        {showAdvancedOptions && (
                            <div className="story-ai-creator-advanced-options">
                                <div className="story-ai-creator-option-row">
                                    <div className="story-ai-creator-option-group">
                                        <label className="story-ai-creator-option-label">Target Audience</label>
                                        <select 
                                            value={targetAudience} 
                                            onChange={(e) => setTargetAudience(e.target.value)}
                                            className="story-ai-creator-select"
                                        >
                                            <option value="">Auto-detect</option>
                                            <option value="gen-z">Gen Z (16-24)</option>
                                            <option value="millennials">Millennials (25-40)</option>
                                            <option value="gen-x">Gen X (41-55)</option>
                                            <option value="professionals">Business Professionals</option>
                                            <option value="entrepreneurs">Entrepreneurs</option>
                                            <option value="students">Students</option>
                                            <option value="parents">Parents</option>
                                        </select>
                                    </div>
                                    
                                    <div className="story-ai-creator-option-group">
                                        <label className="story-ai-creator-option-label">Content Goal</label>
                                        <select 
                                            value={contentGoal} 
                                            onChange={(e) => setContentGoal(e.target.value)}
                                            className="story-ai-creator-select"
                                        >
                                            <option value="">Auto-detect</option>
                                            <option value="viral">Go Viral</option>
                                            <option value="engagement">High Engagement</option>
                                            <option value="educational">Educational</option>
                                            <option value="sales">Drive Sales</option>
                                            <option value="brand-awareness">Brand Awareness</option>
                                            <option value="community">Build Community</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="story-ai-creator-option-group">
                                    <label className="story-ai-creator-option-label">Trend Level</label>
                                    <div className="story-ai-creator-radio-group">
                                        <label className="story-ai-creator-radio">
                                            <input 
                                                type="radio" 
                                                value="trending" 
                                                checked={trendLevel === 'trending'}
                                                onChange={(e) => setTrendLevel(e.target.value)}
                                            />
                                            🔥 Super Trending (Latest viral formats)
                                        </label>
                                        <label className="story-ai-creator-radio">
                                            <input 
                                                type="radio" 
                                                value="current" 
                                                checked={trendLevel === 'current'}
                                                onChange={(e) => setTrendLevel(e.target.value)}
                                            />
                                            📈 Current Trends (Proven formats)
                                        </label>
                                        <label className="story-ai-creator-radio">
                                            <input 
                                                type="radio" 
                                                value="evergreen" 
                                                checked={trendLevel === 'evergreen'}
                                                onChange={(e) => setTrendLevel(e.target.value)}
                                            />
                                            🌿 Evergreen (Timeless content)
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className="story-ai-creator-error">
                            {error}
                        </div>
                    )}

                    <button
                        className="story-ai-creator-generate-button"
                        onClick={handleGenerateStories}
                        disabled={isLoading || (!noIdea && !ideaDescription.trim())}
                    >
                        {isLoading ? (
                            <>
                                <span className="story-ai-creator-loading-spinner"></span>
                                Generating Stories...
                            </>
                        ) : (
                            <>
                                <span className="story-ai-creator-button-icon">✨</span>
                                Generate 8 {contentType?.name || 'Content'} Ideas
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StoryAiCreator; 